import classNames from 'classnames';
import * as React from 'react';
import { useEffect, useState } from 'react';

import { formatDate } from '../../../../helpers/dates';
import debounce from '../../../../helpers/debounce';
import { pluralise } from '../../../../helpers/stringFunctions';
import { UserRole } from '../../../../helpers/userRole';
import Button from '../../../../lib/bootstrap-ui/Button';
import { Booking } from '../../../../models/bookings/Booking';
import { BookingOptionType } from '../../../../models/bookings/BookingOptionType';
import { BookingType } from '../../../../models/bookings/BookingType';
import { useCurrentUser } from '../../../../services/authentication';
import { getBookingAction } from '../../../../services/bookingService';
import { userInRole, userIsAdmin } from '../../../../services/userService';
import Center from '../../../common/Center';
import Icon from '../../../common/Icon';
import MoreOptions from '../../../common/MoreOptions';
import HighlightableTableCell from '../../../common/table/HighlightableTableCell';
import BookingColourSegment from '../../BookingColourSegment';
import BookingOptions from '../../BookingOptions';
import BookingTooltip from '../../tooltip/BookingTooltip';
import BookingTableRowAction, {
    ActionType,
} from '../../update/table/BookingTableRowAction';
import { useClasses } from './BookingTableRow.styles';

interface BookingTableRowProps {
    booking: Booking;
    showAction?: boolean;
    showStore?: boolean;
    showSimplifiedHeaders: boolean;

    onDisplayModal(bookingId: string, modalToDisplay: BookingOptionType): void;
}

const BookingTableNoBookingRow: React.FC<BookingTableRowProps> = ({
    booking,
    showAction = true,
    showStore = true,
    showSimplifiedHeaders,
    onDisplayModal,
}) => {
    const classes = useClasses();

    const user = useCurrentUser();

    const isUserAdmin = userIsAdmin(user);
    const userIsShiftManager = userInRole(user)(UserRole.ShiftManager);

    const disableComplete = !isUserAdmin && !userIsShiftManager;

    const [previousBooking, setPreviousBooking] = useState<Booking | null>(
        null
    );

    const id = booking.recurringBooking ? booking.recurringBooking.id : '';

    const debouncedSetUpdateEnd = debounce(
        () => setPreviousBooking(booking),
        2000
    );

    useEffect(
        () => {
            if (previousBooking !== booking) {
                debouncedSetUpdateEnd();
            }
        },
        // eslint-disable-next-line
        [booking]
    );

    const handleActionClicked = (actionType: ActionType, id: string) => {
        if (actionType === 'Check In') {
            onDisplayModal(id, 'checkIn');
            return;
        }
        onDisplayModal(id, 'checkOut');
    };

    const bookingAction = getBookingAction(booking, false);
    const bookingHasSameIdAsPrevious =
        !!previousBooking && previousBooking.id === booking.id;
    const isRecurring = !!booking.recurringBooking && !!id;
    const hasInstructions = !!booking.bookingInstructions;

    return (
        <tr
            className={classNames(classes.bookingTableRow, {
                [classes.noBookings]:
                    booking.bookingType === BookingType.NoBookings,
                [classes.onSite]:
                    booking.checkedInTime && !booking.checkedOutTime,
            })}
        >
            <td
                className={classes.typeColumn}
                title={booking.bookingType || ''}
            >
                <BookingColourSegment bookingType={booking.bookingType} />
            </td>
            <td>
                <div className={classes.referenceData}>
                    <div className={classes.bookingRefText}>
                        <BookingTooltip
                            options={{ booking, type: 'booking' }}
                            showNoOrdersIcon
                            onTriggerClick={() =>
                                onDisplayModal(booking.id, 'bookingSummary')
                            }
                        />
                    </div>
                    <div
                        style={{
                            display: 'inline-flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Center direction="row">
                            {booking.isTimeSlotOverridden && (
                                <Icon
                                    className={classes.overriddenIcon}
                                    type="overridden"
                                    title="Booking's Time Slot is Overridden"
                                />
                            )}
                            {hasInstructions && (
                                <Button
                                    className={classes.iconButton}
                                    title="Booking Instructions"
                                    element="button"
                                    styleType="link"
                                    onClick={() =>
                                        onDisplayModal(
                                            booking.id,
                                            'instructions'
                                        )
                                    }
                                >
                                    <Icon
                                        className={classes.stickyNoteIcon}
                                        type="sticky-note"
                                    />
                                </Button>
                            )}
                            {isRecurring && (
                                <Button
                                    element="button"
                                    className={classes.iconButton}
                                    styleType="link"
                                    onClick={() =>
                                        onDisplayModal(
                                            booking.id,
                                            'recurringBookingSummary'
                                        )
                                    }
                                    title={booking.recurringBooking?.reference}
                                >
                                    <Icon
                                        className={classes.recurringIcon}
                                        type="recurring-booking"
                                    />
                                </Button>
                            )}
                        </Center>
                    </div>
                </div>
            </td>
            <td colSpan={2}>No Bookings</td>
            <HighlightableTableCell
                highlight={previousBooking?.startDate !== booking.startDate}
                highlightUpdates={bookingHasSameIdAsPrevious}
            >
                {formatDate(booking.startDate, 'HH:mm')}
            </HighlightableTableCell>
            <HighlightableTableCell
                highlight={
                    previousBooking?.hourDuration !== booking.hourDuration
                }
                highlightUpdates={bookingHasSameIdAsPrevious}
            >
                {pluralise('hour', booking.hourDuration)}
            </HighlightableTableCell>
            <HighlightableTableCell
                colspan={showSimplifiedHeaders ? 4 : 9}
                className={classes.restrictedReasonCell}
                highlight={
                    previousBooking?.bookingInstructions !==
                    booking.bookingInstructions
                }
                highlightUpdates={bookingHasSameIdAsPrevious}
                title={booking.bookingInstructions || undefined}
            >
                {booking.bookingInstructions}
            </HighlightableTableCell>
            {showStore && <td>{booking.store}</td>}
            {showAction && (
                <HighlightableTableCell
                    highlightedClassName={classes.updatedLink}
                    highlight={
                        (previousBooking
                            ? getBookingAction(previousBooking)
                            : undefined) !== bookingAction
                    }
                    highlightUpdates={
                        bookingHasSameIdAsPrevious &&
                        bookingAction !== 'Loading'
                    }
                >
                    <BookingTableRowAction
                        booking={booking}
                        bookingAction={bookingAction}
                        disable={disableComplete}
                        onActionInitiated={handleActionClicked}
                    />
                </HighlightableTableCell>
            )}
            <td>
                <MoreOptions>
                    {(close: () => void) => (
                        <BookingOptions
                            bookingType={booking.bookingType!}
                            bookingStatus={booking.status}
                            store={booking.store}
                            onOptionRequested={(type) => {
                                close();
                                onDisplayModal(booking.id, type);
                            }}
                        />
                    )}
                </MoreOptions>
            </td>
        </tr>
    );
};

export default BookingTableNoBookingRow;
